// Drawings for högtidsteman: the falling/rising particles and the header icons.
// Plain SVG, colours only via tokens (the --color-hogtid-* and figure palette,
// theme-neutral like the figures: a pumpkin is orange in every theme).

import type { ReactNode } from "react";
import type { HogtidEffekt } from "@/lib/hogtider";

/** One particle, drawn in a 24 × 24 box, coloured by `farg`. */
export function Partikel({ effekt, farg }: { effekt: HogtidEffekt; farg: string }): ReactNode {
  switch (effekt) {
    case "sno":
      return (
        <g stroke={farg} strokeWidth="1.6" strokeLinecap="round" fill="none">
          {[0, 60, 120].map((v) => (
            <g key={v} transform={`rotate(${v} 12 12)`}>
              <path d="M12 2 V22" />
              <path d="M12 5 L9.5 3 M12 5 L14.5 3 M12 19 L9.5 21 M12 19 L14.5 21" />
            </g>
          ))}
        </g>
      );
    case "hjartan":
      return <path d="M12 21 C5 15.5 2 12 2 8.2 C2 5.2 4.4 3 7.1 3 C9.1 3 11 4.3 12 6.1 C13 4.3 14.9 3 16.9 3 C19.6 3 22 5.2 22 8.2 C22 12 19 15.5 12 21 Z" fill={farg} />;
    case "pi":
      return (
        <text x="12" y="19" textAnchor="middle" fontSize="22" fontFamily="var(--font-heading), Georgia, serif" fill={farg}>
          π
        </text>
      );
    case "semlor":
      return (
        <g>
          <path d="M3 14 C3 19 7 21 12 21 C17 21 21 19 21 14 Z" fill="var(--color-figur-lera)" />
          <path d="M3.5 13.5 C5 11.5 7 12.8 8.5 11.6 C10 12.8 12 11.2 13.5 12.4 C15 11.2 17 12.8 18.5 11.6 C19.6 12.6 20.5 12.4 20.5 13.6 L20.5 14.6 L3.5 14.6 Z" fill="var(--color-figur-havre)" />
          <path d="M5 11.8 C5 6.5 8 4.5 12 4.5 C16 4.5 19 6.5 19 11.8 C16 10.6 8 10.6 5 11.8 Z" fill="var(--color-figur-lera)" />
          <g fill="var(--color-figur-havre)">
            <circle cx="9" cy="7.5" r="0.8" />
            <circle cx="12" cy="6.4" r="0.8" />
            <circle cx="15" cy="7.6" r="0.8" />
            <circle cx="10.6" cy="9.4" r="0.7" />
            <circle cx="13.6" cy="9.2" r="0.7" />
          </g>
        </g>
      );
    case "kanelbullar":
      return (
        <g>
          <circle cx="12" cy="12" r="9.5" fill="var(--color-figur-lera)" />
          <path d="M12 12 m0 -1.6 a1.6 1.6 0 1 1 -1.6 1.6 a3.4 3.4 0 0 1 3.4 -3.4 a5.2 5.2 0 0 1 5.2 5.2 a7 7 0 0 1 -7 7" fill="none" stroke="var(--color-hogtid-pumpa-mork)" strokeWidth="1.3" strokeLinecap="round" />
          <g fill="var(--color-figur-havre)">
            <rect x="7" y="6.5" width="1.6" height="1.2" rx="0.3" />
            <rect x="14.5" y="6" width="1.6" height="1.2" rx="0.3" />
            <rect x="16.4" y="13.6" width="1.6" height="1.2" rx="0.3" />
            <rect x="6.2" y="14.6" width="1.6" height="1.2" rx="0.3" />
            <rect x="11.4" y="17.6" width="1.6" height="1.2" rx="0.3" />
          </g>
        </g>
      );
    case "kladdkakor":
      // a wedge of kladdkaka with icing sugar on top
      return (
        <g>
          <path d="M3 16 L21 9 L21 15 L3 21 Z" fill="var(--color-hogtid-choklad)" />
          <path d="M3 16 L21 9 L21 10.6 L3 17.6 Z" fill="var(--color-hogtid-choklad-ljus)" />
          <path d="M3 16 L12 6 L21 9 Z" fill="var(--color-hogtid-choklad-ljus)" />
          <g fill="var(--color-figur-havre)">
            <circle cx="10" cy="10.5" r="0.9" />
            <circle cx="13.5" cy="9.2" r="0.9" />
            <circle cx="16.5" cy="9.6" r="0.8" />
            <circle cx="8" cy="13" r="0.8" />
            <circle cx="12" cy="12" r="0.7" />
          </g>
        </g>
      );
    case "lussekatter":
      return (
        <g>
          <path d="M8.5 4.5 C4.5 4.5 4 9.5 7.5 9.5 C10 9.5 10 6.5 8 6.8 M15.5 19.5 C19.5 19.5 20 14.5 16.5 14.5 C14 14.5 14 17.5 16 17.2 M8.5 4.5 C13 4.5 11 19.5 15.5 19.5" fill="none" stroke="var(--color-hogtid-saffran)" strokeWidth="3.6" strokeLinecap="round" />
          <circle cx="7.7" cy="7.8" r="1.3" fill="var(--color-hogtid-choklad)" />
          <circle cx="16.3" cy="16.2" r="1.3" fill="var(--color-hogtid-choklad)" />
        </g>
      );
    case "blommor":
      return (
        <g>
          {[0, 60, 120, 180, 240, 300].map((v) => (
            <ellipse key={v} cx="12" cy="6.5" rx="2.6" ry="4.6" transform={`rotate(${v} 12 12)`} fill={farg} />
          ))}
          <circle cx="12" cy="12" r="2.4" fill="var(--color-figur-honung)" />
        </g>
      );
    case "flaggor":
      return (
        <g>
          <rect x="2" y="5" width="20" height="13" rx="0.8" fill="var(--color-hogtid-flagga-bla)" />
          <rect x="7.2" y="5" width="3.2" height="13" fill="var(--color-hogtid-flagga-gul)" />
          <rect x="2" y="9.9" width="20" height="3.2" fill="var(--color-hogtid-flagga-gul)" />
        </g>
      );
    case "spindlar":
    case "kycklingar":
      return null; // drawn by Spindlar.tsx / Kycklingar.tsx
  }
}

/** Colours each effect draws its particles in (one picked per particle). */
export const PARTIKELFARGER: Record<HogtidEffekt, string[]> = {
  sno: ["var(--color-hogtid-sno)"],
  hjartan: ["var(--color-figur-rost)", "var(--color-figur-terrakotta)", "var(--color-figur-plommon)"],
  pi: ["var(--color-figur-salvia)", "var(--color-figur-himmel)", "var(--color-figur-honung)", "var(--color-figur-terrakotta)", "var(--color-figur-plommon)"],
  semlor: [""],
  kanelbullar: [""],
  kladdkakor: [""],
  lussekatter: [""],
  kycklingar: [""],
  blommor: ["var(--color-hogtid-blasippa)", "var(--color-figur-havre)", "var(--color-figur-lavendel)"],
  flaggor: [""],
  spindlar: [""],
};

/** The header button's icon, 32 × 32. `pa`: lit (the theme is on) or not. */
export function HogtidIkon({ effekt, pa }: { effekt: HogtidEffekt; pa: boolean }): ReactNode {
  switch (effekt) {
    case "spindlar":
      return (
        <>
          <path d="M15 8.5 C15 6 15.6 4.2 17.6 3.2 L18.6 4.6 C17.2 5.4 16.9 6.8 17 8.5 Z" fill="var(--color-hogtid-stjalk)" />
          <ellipse cx="9.5" cy="18.5" rx="7" ry="10" fill="var(--color-hogtid-pumpa-mork)" />
          <ellipse cx="22.5" cy="18.5" rx="7" ry="10" fill="var(--color-hogtid-pumpa-mork)" />
          <ellipse cx="12" cy="18.5" rx="6.5" ry="10.5" fill="var(--color-hogtid-pumpa)" />
          <ellipse cx="20" cy="18.5" rx="6.5" ry="10.5" fill="var(--color-hogtid-pumpa)" />
          <ellipse cx="16" cy="18.5" rx="5.5" ry="11" fill="var(--color-hogtid-pumpa)" stroke="var(--color-hogtid-pumpa-mork)" strokeWidth="0.8" />
          <g fill={pa ? "var(--color-hogtid-sken)" : "var(--color-hogtid-pumpa-mork)"}>
            <path d="M8.6 16.4 L11.6 12.6 L13.4 16.4 Z" />
            <path d="M18.6 16.4 L20.4 12.6 L23.4 16.4 Z" />
            <path d="M15 17.6 L16 15.8 L17 17.6 Z" />
            <path d="M7.4 20 C9 24.6 23 24.6 24.6 20 L22.4 21.2 L21.2 20.2 L19.6 22 L18 20.6 L16 22.4 L14 20.6 L12.4 22 L10.8 20.2 L9.6 21.2 Z" />
          </g>
        </>
      );
    case "sno":
      return (
        <g transform="translate(4 4)">
          <Partikel effekt="sno" farg="var(--color-hogtid-sno-ikon)" />
        </g>
      );
    case "hjartan":
      return (
        <g transform="translate(4 5)">
          <Partikel effekt="hjartan" farg="var(--color-figur-rost)" />
        </g>
      );
    case "pi":
      return (
        <text x="16" y="25" textAnchor="middle" fontSize="27" fontFamily="var(--font-heading), Georgia, serif" fill="var(--color-nav-text)">
          π
        </text>
      );
    case "semlor":
      return (
        <g transform="translate(2.5 3) scale(1.13)">
          <Partikel effekt="semlor" farg="" />
        </g>
      );
    case "kanelbullar":
      return (
        <g transform="translate(3 3) scale(1.08)">
          <Partikel effekt="kanelbullar" farg="" />
        </g>
      );
    case "kladdkakor":
    case "lussekatter":
    case "flaggor":
      return (
        <g transform="translate(2.5 2.5) scale(1.12)">
          <Partikel effekt={effekt} farg="" />
        </g>
      );
    case "blommor":
      return (
        <g transform="translate(2.5 2.5) scale(1.12)">
          <Partikel effekt="blommor" farg="var(--color-hogtid-blasippa)" />
        </g>
      );
    case "kycklingar":
      // a painted Easter egg
      return (
        <g>
          <path d="M16 3.5 C22 3.5 25.5 13 25.5 19 C25.5 25 21.5 28.5 16 28.5 C10.5 28.5 6.5 25 6.5 19 C6.5 13 10 3.5 16 3.5 Z" fill="var(--color-hogtid-fjader-gul)" stroke="var(--color-hogtid-pumpa-mork)" strokeWidth="0.8" />
          <path d="M7.2 15.5 Q11.5 12.5 16 15.5 Q20.5 18.5 24.8 15.5" fill="none" stroke="var(--color-hogtid-fjader-rosa)" strokeWidth="2.2" />
          <path d="M6.6 21 Q11.3 18 16 21 Q20.7 24 25.4 21" fill="none" stroke="var(--color-hogtid-fjader-gron)" strokeWidth="2.2" />
        </g>
      );
  }
}
